<template>
  <article
    class="RunView"
    @scroll="handleScroll"
  >
    <RunHeroView
      ref="heroRef"
      v-model="run"
      class="RunView__hero"
      theme="dark"
      isCrewClickable
    />

    <div class="RunView__progressBarContainer">
      <RunProgressBar
        v-bind="{
          run,
          isReady
        }"
      />
    </div>

    <div class="RunView__contentContainer">
      <Transition>
        <div
          v-if="fullRun"
          class="RunView__content"
          data-theme="light"
        >
          <RunStatRow
            v-if="isRunUnlocked"
            ref="statRowRef"
            class="RunView__statRow"
            :class="{ 'RunView__statRow--isStuck': isStatListStuck }"
            v-bind="{
              isCommish,
              inCount,
              maybeCount,
              openSpotCount,
              neededPlayerCount,
            }"
            :waitlistCount="waitlistPlayers.length"
            :pendingCount="pendingPlayers.length"
          />

          <Accordion
            :isExpanded="canInteractWithRun || canReviewRun"
          >
            <RunActionButtonView
              class="RunView__actionButtonList"
              :modelValue="run"
            />
          </Accordion>

          <div
            v-if="isCommish || isInviteButtonVisible"
            class="RunView__manageButtonListContainer"
            data-testid="manageButtonList"
          >
            <RunManageButtonView
              v-if="isCommish"
              class="RunView__manageButtonList"
              v-bind="{run}"
              :orientation="isInviteButtonVisible ? 'vertical' : 'horizontal'"
              @delete="emit('delete')"
            />

            <RunInviteButtonView
              v-if="isInviteButtonVisible"
              class="RunView__inviteButtonList"
              v-bind="{isCommish}"
              :run="fullRun"
            />
          </div>

          <RunPlayerListView
            v-if="isRunUnlocked"
            v-bind="{
              run: fullRun,
              inPlayers,
              maybePlayers,
              outPlayers,
              waitlistPlayers,
              pendingPlayers,
              isCommish,
            }"
            @update:status="addOrReplaceStatus"
            @delete:status="removeStatus"
          />
        </div>
      </Transition>
    </div>
  </article>
</template>

<script lang="ts" setup>
import Accordion from "@/components/Accordion.vue";
import RunProgressBar from "@/components/RunProgressBar.vue";
import RunStatRow from "@/components/RunStatRow.vue";
import useIntersectionDetection from "@/composables/useIntersectionDetection";
import useRealtimeChannel from "@/composables/useRealtimeChannel";
import useRun from "@/composables/useRun";
import useRunPlayers from "@/composables/useRunPlayers";
import useUpdateStatus from "@/composables/useUpdateStatus";
import { supabaseClient } from "@/lib/database/supabase";
import useAuthStore from "@/stores/useAuthStore";
import { RunForCarousel } from "@/stores/useRunCarouselStore";
import RunHeroView from "@/views/RunHeroView.vue";
import RunPlayerListView from "@/views/RunPlayerListView.vue";
import { RunWithAssociations } from "@shared/lib/database/queries/fetchRunWithAssociations";
import { Status, SubRequest } from "@shared/lib/database/query.types";
import { RealtimePostgresDeletePayload, RealtimePostgresInsertPayload, RealtimePostgresUpdatePayload } from "@supabase/supabase-js";
import { useSeoMeta } from "@unhead/vue";
import { storeToRefs } from "pinia";
import { ComponentPublicInstance, computed, ref, toRefs, useTemplateRef } from "vue";
import RunActionButtonView from "./RunActionButtonView.vue";
import RunInviteButtonView from "./RunInviteButtonView.vue";
import RunManageButtonView from "./commish/RunManageButtonView.vue";

const emit = defineEmits<{
  "update:isHeroVisible": [value: boolean],
  delete: [],
}>();

const props = defineProps<{
  runSummary: RunForCarousel,
  fullRun: RunWithAssociations | null,
  isReady: boolean,
}>();

const {
  runSummary,
  fullRun,
  isReady,
} = toRefs(props);

const heroRef = useTemplateRef<ComponentPublicInstance<typeof RunHeroView>>("heroRef");
const statRowRef = useTemplateRef<ComponentPublicInstance<typeof RunStatRow>>("statRowRef");
const statRowStickyTop = 74;
const isStatListStuck = ref(false);

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

useRealtimeChannel("statuses", {
  handleUpsert: handleStatusUpsert,
  handleDelete: handleStatusDelete,
});

useRealtimeChannel("sub_requests", {
  handleUpsert: handleSubRequestUpsert,
  handleDelete: handleSubRequestDelete,
});

const run = computed(() => fullRun.value || runSummary.value);

const {
  formattedShortDate,
  formattedTimeRange,
  openSpotCount,
  neededPlayerCount,
  inCount,
  maybeCount,
} = useRun(run);

const {
  inPlayers,
  maybePlayers,
  outPlayers,
  waitlistPlayers,
  pendingPlayers,
} = useRunPlayers(fullRun);

const {
  currentPlayerStatus,
  canInteractWithRun,
  canReviewRun,
  isCrewPlayer,
  isSeasonRegular,
  isRunUnlocked,
} = useUpdateStatus(run);

useSeoMeta({
  title: () => formattedShortDate.value + ", " + formattedTimeRange.value + " at " + run.value?.court?.name,
});

useIntersectionDetection(heroRef, (isIntersecting) => emit("update:isHeroVisible", isIntersecting));

const crewIds = computed(() => run.value.crews.map((crew) => crew.id));

const isCommish = computed(() => {
  return currentPlayer.value?.crew_memberships.some((membership) => crewIds.value.includes(membership.crew_id) && membership.role === "commissioner");
});

const canInvitePlayer = computed(() => {
  return isCommish.value || !!currentPlayerStatus.value?.probability && (isCrewPlayer.value || isSeasonRegular.value);
});

const isInviteButtonVisible = computed(() => {
  return canInteractWithRun.value && isRunUnlocked.value && canInvitePlayer.value;
});

async function addOrReplaceStatus (_status: Pick<Status, "run_id" | "player_id">) {
  // TODO: return early if timestamps match

  const {
    data:status,
  } = await supabaseClient.from("statuses")
    .select("*, player:players(*)")
    .eq("run_id", _status.run_id)
    .eq("player_id", _status.player_id)
    .limit(1)
    .maybeSingle();

  if (!status || !fullRun.value) return;
  const existingStatus = fullRun.value.statuses.find((_status) => _status.run_id === status.run_id && _status.player_id == status.player_id);

  if (existingStatus) {
    Object.assign(existingStatus, status);
  } else {
    fullRun.value.statuses.push(status);
  }
}

function removeStatus (status: Partial<Status>) {
  // TODO: return early if timestamps match

  if (!fullRun.value) return;
  const index = fullRun.value.statuses.findIndex((_status) => _status.run_id === status.run_id && _status.player_id === status.player_id);

  if (index !== -1) fullRun.value.statuses.splice(index, 1);
}

async function addOrReplaceSubRequest (_subRequest: SubRequest) {
  // TODO: return early if timestamps match

  const {
    data: subRequest,
  } = await supabaseClient.from("sub_requests")
    .select("*, player:players(*), run_invite:run_invites(*, player:players(*))")
    .eq("id", _subRequest.id)
    .limit(1)
    .maybeSingle();

  if (!subRequest || !fullRun.value) return;
  const existingSubRequest = run.value.sub_requests.find((_subRequest) => _subRequest.id === subRequest.id);

  if (existingSubRequest) {
    Object.assign(existingSubRequest, subRequest);
  } else {
    fullRun.value.sub_requests.push(subRequest);
  }
}

function removeSubRequest (subRequest: Partial<SubRequest>) {
  // TODO: return early if timestamps match

  if (!fullRun.value) return;
  const index = fullRun.value.sub_requests.findIndex((_subRequest) => _subRequest.id === subRequest.id);

  if (index !== -1) fullRun.value.sub_requests.splice(index, 1);
}

function handleScroll (event: Event) {
  if (!statRowRef.value) return;

  const el = event.target as HTMLElement;
  const statListTop = statRowRef.value.$el.getBoundingClientRect().top - el.getBoundingClientRect().top;

  isStatListStuck.value = statListTop === statRowStickyTop;
}

function handleStatusUpsert (payload: RealtimePostgresInsertPayload<Status> | RealtimePostgresUpdatePayload<Status>) {
  if (payload.new.run_id !== run.value.id) return;

  addOrReplaceStatus(payload.new);
}

function handleStatusDelete (payload: RealtimePostgresDeletePayload<Status>) {
  if (payload.old.run_id !== run.value.id) return;

  removeStatus(payload.old);
}

function handleSubRequestUpsert (payload: RealtimePostgresInsertPayload<SubRequest> | RealtimePostgresUpdatePayload<SubRequest>) {
  if (payload.new.run_id !== run.value.id) return;

  addOrReplaceSubRequest(payload.new);
}

function handleSubRequestDelete (payload: RealtimePostgresDeletePayload<SubRequest>) {
  removeSubRequest(payload.old);
}
</script>

<style lang="scss" scoped>
.RunView {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding-bottom: var(--safeAreaInsetBottom);
}

.RunView__hero {
  flex: 0 0 auto;
  padding-top: 16px;
}

.RunView__progressBarContainer {
  position: sticky;
  top: 0;
  z-index: 1;

  flex: 0 0 auto;
  padding: 0 16px 24px;

  background-color: var(--themeColorPrimary);
}

.RunView__contentContainer {
  position: relative;
  flex: 1 1 auto;
  background-color: var(--themeColorNeutral0);
}

.RunView__content {
  isolation: isolate;
  display: grid;
  transition: 500ms opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.RunView__statRow {
  position: sticky;
  top: calc(1px * v-bind(statRowStickyTop));
  z-index: 1;

  &::before {
    position: absolute;
    top: 100%;
    left: 0;

    width: 100%;
    height: 8px;

    background: linear-gradient(to bottom, var(--themeDarkTransparent100), transparent);

    transition: 350ms opacity;

    content: "";
  }

  &:not(.RunView__statRow--isStuck)::before {
    opacity: 0;
  }
}

// TODO: find a better way to do this
.RunView__actionButtonList,
.RunView__manageButtonListContainer {
  border-bottom: solid 1px var(--themeColorNeutral100);
}

.RunView__manageButtonListContainer {
  gap: 16px;
  padding: 16px;

  &:has(.FormButton[data-orientation="vertical"]) {
    display: grid;
    grid-auto-flow: column;
  }
}
</style>
